import React, { useEffect, useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { getPointTransactions } from '../services/api';
import {
  Box,
  Typography,
  Chip,
  CircularProgress,
  Alert,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow
} from '@mui/material';
import { format } from 'date-fns';

interface PointTransaction {
  id: number;
  transaction_type: string;
  amount: number;
  live_event_title: string | null;
  memo: string;
  created_at: string;
}

const PointHistory: React.FC = () => {
  const { token, refreshTokenFunction } = useAuth();
  const [transactions, setTransactions] = useState<PointTransaction[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const fetchTransactions = async () => {
    if (!token) return;

    try {
      setLoading(true);
      const response = await getPointTransactions(token);
      setTransactions(response.data);
      setError('');
    } catch (err: any) {
      if (err.response && err.response.status === 401) {
        try {
          const newToken = await refreshTokenFunction();
          if (newToken) {
            const retryResponse = await getPointTransactions(newToken);
            setTransactions(retryResponse.data);
            return;
          }
        } catch (refreshError) {}
      }
      setError('履歴の取得に失敗しました');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchTransactions();
  }, [token]);

  const getTypeLabel = (type: string) => {
    switch (type) {
      case 'purchase': return '付与';
      case 'visit': return '来店';
      case 'redeem': return '交換';
      case 'adjust': return '調整';
      default: return type;
    }
  };

  const getTypeColor = (type: string): 'default' | 'primary' | 'secondary' | 'error' | 'info' | 'success' | 'warning' => {
    switch (type) {
      case 'purchase': return 'info';
      case 'visit': return 'success';
      case 'redeem': return 'warning';
      case 'adjust': return 'secondary';
      default: return 'default';
    }
  };

  if (loading) {
    return (
      <Box 
        display="flex" 
        justifyContent="center" 
        alignItems="center" 
        minHeight="60vh"
      >
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ 
      pt: { xs: 7, sm: 8 },
      minHeight: '100vh' 
    }}>
      {/* ヘッダー */}
      <Typography 
        variant="h6" 
        sx={{ 
          mb: 3,
          textAlign: 'center',
          fontWeight: 'bold',
          fontSize: '1.3rem',
          color: '#d81b60'
        }}
      >
        もマイル履歴
      </Typography>

      {/* エラー表示 */}
      {error && (
        <Alert severity="error" sx={{ mb: 2, mx: 2 }}>
          {error}
        </Alert>
      )}

      {/* 履歴テーブル */}
      {transactions.length === 0 ? (
        <Paper sx={{ p: 4, textAlign: 'center', mx: 2 }}>
          <Typography color="text.secondary">
            履歴がありません
          </Typography>
        </Paper>
      ) : (
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow sx={{ backgroundColor: '#f9f9f9' }}>
                <TableCell sx={{ fontWeight: 'bold' }}>日時</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>種類</TableCell>
                <TableCell align="right" sx={{ fontWeight: 'bold' }}>もマイル</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {transactions.map((transaction) => (
                <TableRow 
                  key={transaction.id}
                  sx={{ '&:hover': { backgroundColor: '#f5f5f5' } }}
                >
                  <TableCell>
                    <Typography variant="body2">
                      {format(new Date(transaction.created_at), 'MM/dd HH:mm')}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={getTypeLabel(transaction.transaction_type)}
                      color={getTypeColor(transaction.transaction_type)}
                      size="small"
                    />
                  </TableCell>
                  <TableCell align="right">
                    <Typography
                      variant="body1"
                      sx={{
                        color: transaction.amount >= 0 ? 'success.main' : 'error.main',
                        fontWeight: 'bold'
                      }}
                    >
                      {transaction.amount >= 0 ? '+' : ''}{transaction.amount}もマイル
                    </Typography>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </Box>
  );
};

export default PointHistory;
